import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Footer from '../components/Footer';
import DownloadProfileModal from '../components/DownloadProfileModal';
import { getLoginUrl } from '../config';
import { extractKeyZipUrl, resolveVpnProfileDownloadUrl } from '../utils/vpnDownloadLinks';

function EmailLogin() {
  const [searchParams] = useSearchParams();
  const email = (searchParams.get('email') || '').trim();
  const pritunlId = (searchParams.get('pritunl_id') || '').trim();

  const missingParams = !email || !pritunlId;
  const [status, setStatus] = useState(() => (missingParams ? 'error' : 'loading'));
  const [errorMessage, setErrorMessage] = useState(() =>
    missingParams
      ? 'This link is missing required information. Please use the link from your email or log in manually.'
      : null
  );
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [openedDownloadModal, setOpenedDownloadModal] = useState(false);
  const authStarted = useRef(false);

  const performEmailLogin = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);

    try {
      const response = await fetch(getLoginUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, pritunl_id: pritunlId }),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        setStatus('error');
        setErrorMessage('An error occurred. Please try again.');
        return;
      }

      const statusCode = data.statusCode ?? response.status;

      if (statusCode === 401) {
        setStatus('error');
        setErrorMessage('The credentials are invalid or this link has expired.');
        return;
      }

      if (statusCode === 402) {
        setStatus('error');
        setErrorMessage('Your access has expired. Please purchase a new plan.');
        return;
      }

      if (statusCode === 200 || response.ok) {
        setStatus('done');
        const responseData = data.body || data;
        const keyZipUrl = extractKeyZipUrl(responseData.download_links);
        if (keyZipUrl) {
          setOpenedDownloadModal(true);
          setDownloadUrl(resolveVpnProfileDownloadUrl(keyZipUrl));
        }
        return;
      }

      setStatus('error');
      setErrorMessage(`An error occurred (Status: ${statusCode}). Please try again.`);
    } catch (err) {
      console.error('Email login error:', err);
      setStatus('error');
      setErrorMessage('An error occurred. Please try again.');
    }
  }, [email, pritunlId]);

  useDocumentTitle(status === 'error' ? 'VPN profile' : 'Preparing your VPN profile');
  useScripts();

  useEffect(() => {
    document.body.classList.add('login');
    return () => document.body.classList.remove('login');
  }, []);

  useEffect(() => {
    if (missingParams) return;
    if (authStarted.current) return;
    authStarted.current = true;
    void performEmailLogin();
  }, [missingParams, performEmailLogin]);

  return (
    <>
      <div
        className="bg-form"
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <div className="container">
          <div
            id="email-login-panel"
            className="form-contact animatedParent"
            style={{
              maxWidth: '520px',
              margin: '0 auto',
              padding: '48px 40px',
              textAlign: 'center',
            }}
          >
            {status === 'loading' && (
              <>
                <div
                  className="email-login-spinner"
                  style={{
                    width: '48px',
                    height: '48px',
                    margin: '0 auto 24px',
                    border: '4px solid #b3d9ff',
                    borderTopColor: '#2563eb',
                    borderRadius: '50%',
                    animation: 'email-login-spin 0.8s linear infinite',
                  }}
                  aria-hidden
                />
                <style>{`
                  @keyframes email-login-spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <h1 style={{ margin: '0 0 16px', fontSize: '24px', fontWeight: 700, color: '#1a3a4a' }}>
                  Preparing your VPN profile
                </h1>
                <p style={{ margin: 0, fontSize: '16px', color: '#4a6572', lineHeight: 1.6 }}>
                  Generating your personal VPN profile links for…
                </p>
                <div
                  style={{
                    marginTop: '14px',
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    textAlign: 'center',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <strong
                    style={{
                      color: '#2563eb',
                      fontSize: '16px',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}
                  >
                    {email}
                  </strong>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <h1 style={{ margin: '0 0 16px', fontSize: '24px', fontWeight: 700, color: '#1a3a4a' }}>
                  Unable to continue
                </h1>
                <p style={{ margin: '0 0 24px', fontSize: '16px', color: '#4a6572', lineHeight: 1.6 }}>
                  {errorMessage}
                </p>
                {missingParams ? (
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    <Link to="/login" style={{ color: '#2563eb', fontWeight: 500 }}>
                      Go to login
                    </Link>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={() => void performEmailLogin()}
                    style={{
                      padding: '12px 28px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#2563eb',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                )}
              </>
            )}

            {status === 'done' && downloadUrl && (
              <p style={{ margin: 0, fontSize: '16px', color: '#4a6572', lineHeight: 1.6 }}>
                Use the window above to download your VPN profile.
              </p>
            )}

            {status === 'done' && !downloadUrl && openedDownloadModal && (
              <p style={{ margin: 0, fontSize: '16px', color: '#4a6572', lineHeight: 1.6 }}>
                You closed the download window. Refresh this page if you need the link again, or use the{' '}
                <Link to="/login" style={{ color: '#2563eb', fontWeight: 500 }}>
                  login page
                </Link>
                .
              </p>
            )}

            {status === 'done' && !downloadUrl && !openedDownloadModal && (
              <>
                <h1 style={{ margin: '0 0 16px', fontSize: '24px', fontWeight: 700, color: '#1a3a4a' }}>
                  Signed in
                </h1>
                <p style={{ margin: '0 0 20px', fontSize: '16px', color: '#4a6572', lineHeight: 1.6 }}>
                  Your session is ready, but a download link could not be loaded. Try logging in with your
                  password or use the email we sent you.
                </p>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  <Link to="/login" style={{ color: '#2563eb', fontWeight: 500 }}>
                    Go to login
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {downloadUrl && (
        <DownloadProfileModal
          downloadUrl={downloadUrl}
          onClose={() => setDownloadUrl(null)}
        />
      )}

      <Footer />
    </>
  );
}

export default EmailLogin;
